// Every failure that leaves the API layer is an ApiError. Components read
// `error.message` / `error.status` — nothing outside this file parses an axios
// error shape. FRONTEND_RULE_BOOK.md §3.
//
// The fields are the error envelope's (backend/errors/README.md): `code` is
// the seven-digit dispatch key and the only thing worth branching on, and
// `request_id` is the support handle — it is what every toast and error card
// shows, and what pastes into the obs dashboard to find the failing request.
import axios from "axios";

/** One element of the envelope's `details`. A validation failure (1010004) sends
    `{field, issue}`; a few codes send a shape of their own (3100004 names who
    still uses a connection), so a reader checks `code` before trusting one. */
export interface ApiErrorDetail {
  field?: string;
  issue?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: number | null = null,
    public request_id: string | null = null,
    public details?: ApiErrorDetail[],
    public debug?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/* What an error body might hold. Every field is `unknown` on purpose: a proxy,
   a crashed worker or a 502 page answers with whatever it likes, and a toast
   that renders `[object Object]` is its own kind of silent failure. */
interface ErrorBody {
  code?: unknown;
  message?: unknown;
  request_id?: unknown;
  details?: ApiErrorDetail[];
  debug?: unknown;
}

const text = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

export function normalizeError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (axios.isAxiosError(err)) {
    // Two ways to get no response at all, and they read very differently to
    // whoever is waiting, so they get different words. Status 0 = never answered.
    if (err.code === "ECONNABORTED") return new ApiError("Request timed out", 0);
    if (!err.response) return new ApiError("Network error — the API did not answer", 0);

    const body = (err.response.data ?? {}) as ErrorBody;
    return new ApiError(
      text(body.message) ?? "Something went wrong",
      err.response.status,
      typeof body.code === "number" ? body.code : null,
      text(body.request_id),
      body.details,
      body.debug,
    );
  }
  return new ApiError(err instanceof Error ? err.message : "Something went wrong", 0);
}
